import { SUPER_ADMIN } from './roles';

/**
 * The gated admin-panel sections (Settings > Section access).
 *
 * The first two - Activation codes and Vouchers - can be opened by a super admin to Doctors and Admins,
 * or to Admins only ("Admins and SuperAdmins"). This is HIDE ONLY: a section someone may not see is left
 * out of their menu and its URL sends them back to the Appointments page. Their APIs keep the server
 * access they always had - a Doctor kept out of an "Admins and SuperAdmins" page can still call its API.
 *
 * Statistics, Staff activity and Log history (the staff log) are different: ALWAYS super admins only
 * (superAdminOnly below). Section access lists them as locked rows that cannot be changed, and the menu
 * and their URLs are only ever open to a super admin. (Statistics: Sunil, 23 Oct 2026 - Admins must
 * never open it. The server refuses anyone else Statistics, Staff activity and Log history.)
 *
 * Clinicians management is ALWAYS Admins and super admins, never Doctors (adminsAndSuperAdmins below;
 * Sunil, 24 Oct 2026): a locked row too, that cannot be changed. The server decides - it answers
 * visible-sections true for an Admin and false for a Doctor - and refuses a Doctor the clinician APIs.
 * An Admin may create Doctors and edit Doctors (never an Admin, a super admin or themselves); Change
 * role, Sign out everywhere and History stay super admins only.
 */
export const SECTIONS = {
    CLINICIANS: 'clinicians',
    ACTIVATION_CODES: 'activationCodes',
    VOUCHERS: 'vouchers',
    STATISTICS: 'statistics',
    STAFF_ACTIVITY: 'staffActivity',
    STAFF_LOG: 'staffLog',
};

/**
 * Who may see a section (super admins always see every section):
 *  - 'all' = Doctors and Admins;
 *  - 'admins' = Admins and super admins - not Doctors;
 *  - 'superAdmin' = super admins only. Also what anything else the server stores means.
 * The menu never reads these: the server works out who sees what and answers visible-sections with
 * one true/false per section (normaliseSections).
 */
export const SECTION_AUDIENCE = {
    ALL: 'all',
    ADMINS: 'admins',
    SUPER_ADMIN: 'superAdmin',
};

/** The choices a settable section offers on the Section access card, widest first. */
export const SETTABLE_AUDIENCES = [SECTION_AUDIENCE.ALL, SECTION_AUDIENCE.ADMINS, SECTION_AUDIENCE.SUPER_ADMIN];

/**
 * In the order the server lists them (and the Settings card shows them). `superAdminOnly`: never
 * visible to a Doctor or Admin, whatever any answer says, and never changeable in Section access.
 * `adminsAndSuperAdmins`: a fixed rule, Admins and super admins - the server's answer decides who sees
 * it (as for a settable section), and it is never changeable in Section access.
 * `audiences`: the choices Section access offers for a settable section.
 */
export const SECTION_LIST = [
    { key: SECTIONS.ACTIVATION_CODES, label: 'Activation codes', path: '/activation-code', audiences: SETTABLE_AUDIENCES },
    { key: SECTIONS.VOUCHERS, label: 'Vouchers', path: '/voucher', audiences: SETTABLE_AUDIENCES },
    { key: SECTIONS.STATISTICS, label: 'Statistics', path: '/statistics', superAdminOnly: true },
    { key: SECTIONS.CLINICIANS, label: 'Clinicians management', path: '/clinicians-management', adminsAndSuperAdmins: true },
    { key: SECTIONS.STAFF_ACTIVITY, label: 'Staff activity', path: '/staff-activity', superAdminOnly: true },
    { key: SECTIONS.STAFF_LOG, label: 'Log history', path: '/log-history', superAdminOnly: true },
];

export const SECTION_LOCKED_MESSAGE = 'That section is limited to super admins.';

/**
 * Who a locked section is always open to (on the Section access card): 'admins' for an
 * adminsAndSuperAdmins section, 'superAdmin' for a superAdminOnly one, null for a settable section.
 */
export function fixedAudience({ superAdminOnly, adminsAndSuperAdmins }) {
    if (superAdminOnly) {
        return SECTION_AUDIENCE.SUPER_ADMIN;
    }

    return adminsAndSuperAdmins ? SECTION_AUDIENCE.ADMINS : null;
}

/**
 * The notice when a Doctor or Admin is sent back from a gated section although no check has ever
 * succeeded for them: the stored-role fallback shows nothing, so it may not be locked at all.
 */
export const SECTION_CHECK_FAILED_MESSAGE = 'Could not check your access to that section. Please try again.';

/** Every section set to one value. */
export function everySection(visible) {
    return SECTION_LIST.reduce((sections, { key }) => ({ ...sections, [key]: visible }), {});
}

/**
 * What to show before the server has answered, or when it could not: a super admin sees
 * everything (the server would say so too), anyone else sees none of the gated sections.
 * Fails closed.
 */
export function fallbackSections(role) {
    return everySection(role === SUPER_ADMIN);
}

/**
 * The server's answer for a Doctor or Admin (a super admin sees every section), with anything missing
 * or not strictly true read as hidden - and a super-admin-only section always hidden, even if an
 * answer said otherwise. Clinicians management (adminsAndSuperAdmins) follows the answer: the server
 * says true for an Admin and false for a Doctor.
 */
export function normaliseSections(sections) {
    return SECTION_LIST.reduce(
        (result, { key, superAdminOnly }) => ({
            ...result,
            [key]: !superAdminOnly && Boolean(sections) && sections[key] === true,
        }),
        {},
    );
}
